import { invariantResponse } from '@epic-web/invariant'
import { Img } from 'openimg/react'
import {
	type LoaderFunctionArgs,
	Form,
	Link,
	useLoaderData,
} from 'react-router'
import { GeneralErrorBoundary } from '#app/components/error-boundary.tsx'
import { Spacer } from '#app/components/spacer.tsx'
import { Icon } from '#app/components/ui/icon.tsx'
import { prisma } from '#app/utils/db.server.ts'
import { SITE_NAME } from '#app/utils/branding.ts'
import { getUserImgSrc } from '#app/utils/misc.tsx'
import { useOptionalUser } from '#app/utils/user.ts'
import { type Route } from './+types/index.ts'

export async function loader({ params }: LoaderFunctionArgs) {
	const user = await prisma.user.findFirst({
		select: {
			id: true,
			name: true,
			username: true,
			createdAt: true,
			image: { select: { id: true, objectKey: true } },
		},
		where: {
			username: params.username,
		},
	})

	invariantResponse(user, 'User not found', { status: 404 })

	return { user, userJoinedDisplay: user.createdAt.toLocaleDateString() }
}

export default function ProfileRoute() {
	const data = useLoaderData<typeof loader>()
	const user = data.user
	const userDisplayName = user.name ?? user.username
	const loggedInUser = useOptionalUser()
	const isLoggedInUser = user.id === loggedInUser?.id

	return (
		<div
			className="min-h-screen px-4 py-6 md:px-8 md:py-8 bg-cathedral-page"
		>
			<div className="mx-auto max-w-3xl">
				{/* Navigation */}
				<nav className="flex items-center justify-between px-2 py-2">
					<Link
						to="/"
						className="cathedral-heading text-xs tracking-[0.12em] uppercase text-cathedral-brown transition-colors hover:text-cathedral-gold"
					>
						{SITE_NAME}
					</Link>
					{isLoggedInUser ? (
						<Link
							to="/dashboard"
							className="cathedral-heading text-xs tracking-[0.14em] uppercase text-cathedral-gold transition-colors hover:text-cathedral-gold-light rounded-sm border border-cathedral-gold/30 px-4 py-2"
						>
							Dashboard
						</Link>
					) : null}
				</nav>

				<Spacer size="xs" />

				{/* Profile Card */}
				<div className="rounded-xl border bg-cathedral-card px-6 py-10 md:px-10 md:py-14">
					{/* Avatar */}
					<div className="flex justify-center">
						<div className="relative">
							<div className="size-44 overflow-hidden rounded-full ring-2 ring-gold/20 ring-offset-4 ring-offset-bronze-light md:size-52">
								<Img
									src={getUserImgSrc(data.user.image?.objectKey)}
									alt={userDisplayName}
									className="h-full w-full object-cover"
									width={832}
									height={832}
									isAboveFold
								/>
							</div>
						</div>
					</div>

					<Spacer size="2xs" />

					{/* Name & Info */}
					<div className="flex flex-col items-center text-center">
						<h1 className="cathedral-heading text-cathedral-dark text-3xl font-semibold tracking-wide md:text-4xl">
							{userDisplayName}
						</h1>
						<p className="cathedral-body text-cathedral-brown mt-2 text-base">
							Joined {data.userJoinedDisplay}
						</p>
					</div>

					{/* Divider */}
					<div className="mx-auto my-8 flex max-w-xs items-center gap-4" aria-hidden>
						<div className="h-px flex-1 bg-gradient-to-r from-transparent via-gold/30 to-transparent" />
						<div className="h-2 w-2 rotate-45 bg-cathedral-bright/40" />
						<div className="h-px flex-1 bg-gradient-to-r from-transparent via-gold/30 to-transparent" />
					</div>

					{/* Actions */}
					<div className="flex flex-col items-center gap-4">
						{isLoggedInUser ? (
							<>
								<div className="flex flex-wrap justify-center gap-3">
									<Link
										to="notes"
										prefetch="intent"
										className="cathedral-heading inline-flex items-center gap-2 rounded-sm border-2 border-cathedral-gold bg-cathedral-bright px-6 py-2.5 text-xs tracking-[0.15em] uppercase text-cathedral-deep transition-opacity hover:opacity-90"
									>
										<Icon name="pencil-1" className="size-3.5" />
										My notes
									</Link>
									<Link
										to="/settings/profile"
										prefetch="intent"
										className="cathedral-heading inline-flex items-center gap-2 rounded-sm border-2 border-cathedral-gold px-6 py-2.5 text-xs tracking-[0.15em] uppercase text-cathedral-gold transition-colors hover:bg-cathedral-bright/10"
									>
										<Icon name="avatar" className="size-3.5" />
										Edit profile
									</Link>
								</div>

								<Form action="/logout" method="POST" className="mt-2">
									<button
										type="submit"
										className="cathedral-body text-sm text-cathedral-brown underline-offset-4 transition-colors hover:text-cathedral-gold hover:underline"
									>
										<Icon name="exit" className="size-3.5">
											Sign out
										</Icon>
									</button>
								</Form>
							</>
						) : (
							<Link
								to="notes"
								prefetch="intent"
								className="cathedral-heading inline-flex items-center gap-2 rounded-sm border-2 border-cathedral-gold bg-cathedral-bright px-6 py-2.5 text-xs tracking-[0.15em] uppercase text-cathedral-deep transition-opacity hover:opacity-90"
							>
								<Icon name="pencil-1" className="size-3.5" />
								{userDisplayName}'s notes
							</Link>
						)}
					</div>
				</div>
			</div>
		</div>
	)
}

export const meta: Route.MetaFunction = ({ data, params }) => {
	const displayName = data?.user.name ?? params.username
	return [
		{ title: `${displayName} | ${SITE_NAME}` },
		{
			name: 'description',
			content: `Profile of ${displayName} on ${SITE_NAME}`,
		},
	]
}

export function ErrorBoundary() {
	return (
		<GeneralErrorBoundary
			statusHandlers={{
				404: ({ params }) => (
					<p>No user with the username "{params.username}" exists</p>
				),
			}}
		/>
	)
}
